import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "addressSearch",
    "addressSelect",
    "searchResults",
    "selectedAddress",
    "selectedAddressText",
    "contributionField",
    "contributionDisplay",
    "monthsGrid",
    "monthCheckbox",
    "monthSelectionContent",
    "arrearsSection",
    "arrearsGrid",
    "arrearsCount"
  ]

  static values = {
    year: Number,
    currentContributionRate: Number
  }

  static classes = ["yearNavigation"]

  connect() {
    console.log("UserContributionForm controller connected")
    this.currentContributionRateValue = 0
    this.monthlyRates = new Map() // Store month-specific contribution rates
    this.searchTimeout = null
    
    // Check if there's a selected address from turbo stream
    this.checkForSelectedAddress()
    
    // Add click outside handler
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
  }

  checkForSelectedAddress() {
    // Check if month selection content has a selected address data attribute
    if (this.hasMonthSelectionContentTarget) {
      const selectedAddress = this.monthSelectionContentTarget.dataset.selectedAddress
      if (selectedAddress && selectedAddress !== '' && this.hasAddressSelectTarget) {
        console.log("Restoring selected address:", selectedAddress)
        // Set the address select value
        this.addressSelectTarget.value = selectedAddress
        // Fetch payment status for this address
        this.fetchPaymentStatus(selectedAddress)
      }
    }
  }

  searchAddresses(event) {
    const query = event.target.value.trim()
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Hide results if query is too short
    if (query.length < 2) {
      this.searchResultsTarget.classList.add('hidden')
      return
    }
    
    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  performSearch(query) {
    const url = `/user_contributions/search_addresses?q=${encodeURIComponent(query)}`
    
    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displaySearchResults(data)
    })
    .catch(error => {
      console.error('Error searching addresses:', error)
    })
  }

  displaySearchResults(addresses) {
    const resultsContainer = this.searchResultsTarget
    
    if (addresses.length === 0) {
      resultsContainer.innerHTML = '<div class="p-3 text-sm text-gray-500">Tidak ada alamat yang ditemukan</div>'
    } else {
      resultsContainer.innerHTML = addresses.map(address => `
        <button type="button" 
                class="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                data-action="click->user-contribution-form#selectAddress"
                data-address-id="${address.id}"
                data-address-text="${address.display_text}">
          <div class="font-medium text-gray-900">${address.block_address}</div>
          <div class="text-sm text-gray-500">${address.head_of_family_name || 'Tidak ada nama kepala keluarga'}</div>
        </button>
      `).join('')
    }
    
    resultsContainer.classList.remove('hidden')
  }

  selectAddress(event) {
    const button = event.currentTarget
    const addressId = button.dataset.addressId
    const addressText = button.dataset.addressText
    
    console.log('Address selected:', { addressId, addressText })
    
    // Set the hidden field value
    this.addressSelectTarget.value = addressId
    
    // Update display
    this.selectedAddressTextTarget.textContent = addressText
    this.selectedAddressTarget.classList.remove('hidden')
    
    // Clear search input and hide results
    this.addressSearchTarget.value = ''
    this.searchResultsTarget.classList.add('hidden')
    
    // Trigger address changed event
    this.addressChanged()
  }

  clearSelection() {
    // Clear selection
    this.addressSelectTarget.value = ''
    this.selectedAddressTarget.classList.add('hidden')
    this.addressSearchTarget.value = ''
    this.searchResultsTarget.classList.add('hidden')
    
    // Clear month selection
    const monthSelectionContainer = document.getElementById('month-selection')
    if (monthSelectionContainer) {
      monthSelectionContainer.innerHTML = ''
    }
    
    this.resetForm()
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.searchResultsTarget.classList.add('hidden')
    }
  }

  async addressChanged() {
    const addressId = this.addressSelectTarget.value
    
    if (!addressId) {
      this.resetForm()
      return
    }

    await this.fetchPaymentStatus(addressId)
  }

  yearNavigation(event) {
    event.preventDefault()

    const newYear = parseInt(event.currentTarget.dataset.year)
    const currentAddressId = this.addressSelectTarget?.value

    console.log("Year navigation clicked:", newYear, "Address:", currentAddressId)

    // Build URL with year and address parameters
    const url = new URL(window.location.origin + '/user_contributions/new')
    url.searchParams.set('year', newYear)
    if (currentAddressId) {
      url.searchParams.set('address_id', currentAddressId)
    }

    // Simple page navigation - browser will reload with new parameters
    window.location.href = url.toString()
  }

  // This method is called when monthSelectionContent target is connected/reconnected
  monthSelectionContentTargetConnected() {
    console.log("Month selection content target connected")
    // Small delay to ensure DOM is fully updated
    setTimeout(() => {
      this.checkForSelectedAddress()
    }, 50)
  }

  async fetchPaymentStatus(addressId) {
    try {
      const url = `/user_contributions/payment_status?address_id=${addressId}&year=${this.yearValue}`
      console.log('Fetching payment status from:', url)

      const response = await fetch(url)
      const data = await response.json()

      console.log('Payment Status Response:', data)

      if (data.success && data.months) {
        this.updateContributionInfo(data.months[0])
        this.updateMonthsWithPaymentStatus(data.months)

        // Load arrears if available
        if (data.arrears && data.arrears > 0) {
          this.loadArrears(data.arrears, data.arrears_rate || 175000)
        } else {
          this.hideArrears()
        }
      } else {
        console.warn('API returned no payment status or failed:', data)
        this.resetForm()
      }

    } catch (error) {
      console.error('Error fetching payment status:', error)
      this.resetForm()
    }
  }

  loadArrears(count, rate) {
    if (!this.hasArrearsSectionTarget) return

    console.log(`Loading ${count} arrears at rate ${rate}`)

    // Store arrears rate for calculation
    this.arrearsRate = rate

    // Show arrears section
    this.arrearsSectionTarget.style.display = 'block'
    this.arrearsCountTarget.textContent = count

    // Build arrears checkboxes
    let arrearsHTML = ''
    for (let i = 1; i <= count; i++) {
      const formattedRate = new Intl.NumberFormat('id-ID').format(rate)
      arrearsHTML += `
        <label class="relative flex items-center p-3 bg-white border-2 border-red-200 rounded-lg cursor-pointer hover:border-red-400 hover:shadow-md transition-all">
          <input type="checkbox"
                 name="pay_arrears[]"
                 value="${i}"
                 data-arrears-rate="${rate}"
                 data-action="change->user-contribution-form#updateTotalContribution"
                 class="sr-only peer">
          <div class="flex-1 text-center">
            <div class="text-sm font-bold text-gray-900">Tunggakan ${i}</div>
            <div class="text-xs text-red-700 mt-1">Rp ${formattedRate}</div>
          </div>
          <div class="absolute top-1 right-1 w-5 h-5 border-2 border-red-400 rounded-full peer-checked:bg-red-500 peer-checked:border-red-500 flex items-center justify-center">
            <svg class="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
          </div>
        </label>
      `
    }

    this.arrearsGridTarget.innerHTML = arrearsHTML
  }

  hideArrears() {
    if (!this.hasArrearsSectionTarget) return
    this.arrearsSectionTarget.style.display = 'none'
  }

  getCheckedArrearsCount() {
    if (!this.hasArrearsGridTarget) return 0
    const arrearsCheckboxes = this.arrearsGridTarget.querySelectorAll('input[type="checkbox"]:checked')
    return arrearsCheckboxes.length
  }

  updateContributionInfo(firstMonth) {
    if (!firstMonth) return

    // Convert to number to ensure proper calculation
    this.currentContributionRateValue = parseFloat(firstMonth.contribution_rate)

    console.log('Updated contribution rate for address:', this.currentContributionRateValue)

    // Clear and reset the contribution field
    if (this.hasContributionFieldTarget) {
      this.contributionFieldTarget.value = ''
      // Trigger total calculation after contribution rate is updated
      setTimeout(() => {
        this.updateTotalContribution()
      }, 100)
    }
  }

  updateMonthsWithPaymentStatus(months) {
    // Clear previous monthly rates
    this.monthlyRates.clear()

    months.forEach(monthData => {
      const monthId = `month_${monthData.month}_${monthData.year}`
      const monthContainer = document.getElementById(monthId)?.parentElement

      // Store the monthly rate for later calculation (convert to number)
      const monthKey = `${monthData.month}_${monthData.year}`
      this.monthlyRates.set(monthKey, parseFloat(monthData.contribution_rate))

      if (monthContainer) {
        const label = monthContainer.querySelector('.month-label')
        const contributionDisplay = monthContainer.querySelector('.contribution-display')
        const paymentStatus = monthContainer.querySelector('.payment-status')
        const paidIndicator = monthContainer.querySelector('.paid-indicator')
        const unpaidIndicator = monthContainer.querySelector('.unpaid-indicator')
        const checkbox = monthContainer.querySelector('.month-checkbox')

        // Update contribution amount display
        if (contributionDisplay && monthData.contribution_rate > 0) {
          contributionDisplay.textContent = `Rp ${this.formatCurrency(monthData.contribution_rate)}`
          contributionDisplay.classList.remove('hidden')
        }
        
        // Update payment status
        if (paymentStatus) {
          paymentStatus.classList.remove('hidden')
          
          if (monthData.is_paid) {
            // Already paid - show paid status and disable checkbox
            paidIndicator?.classList.remove('hidden')
            unpaidIndicator?.classList.add('hidden')
            
            // Style as paid
            label?.classList.add('bg-green-50', 'border-green-200', 'text-green-800')
            label?.classList.remove('border-gray-200', 'hover:bg-gray-50')
            
            // Disable checkbox since already paid
            if (checkbox) {
              checkbox.disabled = true
              checkbox.checked = false
            }
          } else {
            // Not paid - show unpaid status and enable checkbox
            paidIndicator?.classList.add('hidden')
            unpaidIndicator?.classList.remove('hidden')
            
            // Style as unpaid
            label?.classList.add('bg-red-50', 'border-red-200')
            label?.classList.remove('border-gray-200', 'bg-green-50', 'border-green-200', 'text-green-800')
            
            // Enable checkbox for selection
            if (checkbox) {
              checkbox.disabled = false
            }
          }
        }
      }
    })
    
    console.log('Monthly rates stored:', Object.fromEntries(this.monthlyRates))
  }

  monthToggled(event) {
    const checkbox = event.target
    const label = document.querySelector(`label[for="${checkbox.id}"]`)
    
    if (checkbox.checked) {
      label?.classList.add('ring-2', 'ring-indigo-500')
    } else {
      label?.classList.remove('ring-2', 'ring-indigo-500')
    }
    
    this.updateTotalContribution()
  }

  updateTotalContribution() {
    const checkedBoxes = this.monthCheckboxTargets.filter(checkbox =>
      checkbox.checked && !checkbox.disabled
    )

    let totalAmount = 0
    let monthsDetails = []

    // Calculate total from regular months
    checkedBoxes.forEach(checkbox => {
      const month = parseInt(checkbox.dataset.month)
      const year = parseInt(checkbox.dataset.year)
      const monthKey = `${month}_${year}`

      // Get the specific contribution rate for this month/year
      const monthlyRate = this.monthlyRates.get(monthKey) || this.currentContributionRateValue

      totalAmount += monthlyRate
      monthsDetails.push({
        month: month,
        year: year,
        rate: monthlyRate
      })
    })

    // Add arrears amount
    if (this.hasArrearsGridTarget) {
      const arrearsCheckboxes = this.arrearsGridTarget.querySelectorAll('input[type="checkbox"]:checked')
      arrearsCheckboxes.forEach(checkbox => {
        const arrearsRate = parseFloat(checkbox.dataset.arrearsRate) || this.arrearsRate || 0
        totalAmount += arrearsRate
      })
    }

    // Update both the hidden field (for form submission) and display field (for user)
    if (this.hasContributionFieldTarget) {
      if ((checkedBoxes.length > 0 || this.getCheckedArrearsCount() > 0) && totalAmount > 0) {
        this.contributionFieldTarget.value = totalAmount
        if (this.hasContributionDisplayTarget) {
          this.contributionDisplayTarget.value = this.formatCurrency(totalAmount)
        }
      } else {
        this.contributionFieldTarget.value = ''
        if (this.hasContributionDisplayTarget) {
          this.contributionDisplayTarget.value = ''
        }
      }
    }

    console.log('Total contribution calculated:', {
      selectedMonths: checkedBoxes.length,
      monthsDetails: monthsDetails,
      totalAmount: totalAmount
    })
  }

  resetForm() {
    // Clear contribution fields
    if (this.hasContributionFieldTarget) {
      this.contributionFieldTarget.value = ''
    }
    if (this.hasContributionDisplayTarget) {
      this.contributionDisplayTarget.value = ''
    }

    // Reset all month displays
    this.monthCheckboxTargets.forEach(checkbox => {
      const monthContainer = checkbox.parentElement
      const label = monthContainer.querySelector('.month-label')
      const contributionDisplay = monthContainer.querySelector('.contribution-display')
      const paymentStatus = monthContainer.querySelector('.payment-status')

      // Reset styles
      label?.classList.remove('bg-green-50', 'border-green-200', 'text-green-800', 'bg-red-50', 'border-red-200')
      label?.classList.add('border-gray-200', 'hover:bg-gray-50')

      // Hide displays
      contributionDisplay?.classList.add('hidden')
      paymentStatus?.classList.add('hidden')

      // Enable and uncheck checkbox
      checkbox.disabled = false
      checkbox.checked = false
    })

    this.currentContributionRateValue = 0
    this.monthlyRates.clear() // Clear monthly rates
  }

  formatCurrency(amount) {
    if (!amount || amount === 0) return '0'

    // Convert to integer to remove any decimal places
    const intAmount = parseInt(amount)

    // Format with Indonesian locale (using period as thousands separator)
    return intAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }

}